import SectionHeading from "@/components/SectionHeading";
import Tag from "@/components/Tag";

import {
  getCareerRoleDisplayName,
  getRoleFitCompatibilityTierLabel,
  getTopCompatibleRoles,
  type CareerRoleFit
} from "./experienceIndexResults";

const TOP_COMPATIBLE_ROLES_HEADING = {
  heading: "Top Compatible Roles",
  body: "Strongest role matches from this person's completed answers. Compatibility is generalized and projected, not guaranteed."
};

const TOP_COMPATIBLE_ROLES_EMPTY_COPY = "Compatible roles will appear here.";

function RoleFitCard({ isComparisonRole, roleFit }: { isComparisonRole: boolean; roleFit: CareerRoleFit }) {
  const displayName = getCareerRoleDisplayName(roleFit);

  return (
    <div
      className="flex h-full flex-col rounded-lg border border-sys-brand-outline bg-sys-brand-surface p-5"
      data-testid={`career-plan-top-compatible-role-${roleFit.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-body-large-semibold text-sys-brand-on-surface">{displayName}</p>
          <p className="mt-1 text-body-small text-sys-brand-on-surface-variant">
            {getRoleFitCompatibilityTierLabel(roleFit.fitPercent)}
          </p>
          {isComparisonRole ? (
            <div className="mt-2" data-testid={`career-plan-top-compatible-role-${roleFit.id}-comparison-role-tag`}>
              <Tag tagType="success" tagStyle="standard" size="small">
                Comparison Role
              </Tag>
            </div>
          ) : null}
        </div>
        <p className="shrink-0 text-title-large-bold text-sys-brand-primary">{roleFit.fitPercent}%</p>
      </div>
      <p className="mt-4 text-body-small text-sys-brand-on-surface">
        {roleFit.matchedCompetencyCount} of {roleFit.completedCompetencyCount} completed competencies meet this role's
        rubric.
      </p>
    </div>
  );
}

export default function ExperienceIndexTopCompatibleRolesSection({
  comparisonRoleId,
  roleFits
}: {
  comparisonRoleId: string;
  roleFits: CareerRoleFit[];
}) {
  const topCompatibleRoles = getTopCompatibleRoles(roleFits);

  return (
    <div data-testid="career-plan-top-compatible-roles">
      <SectionHeading heading={TOP_COMPATIBLE_ROLES_HEADING.heading} body={TOP_COMPATIBLE_ROLES_HEADING.body} />
      {topCompatibleRoles.length > 0 ? (
        <div className="grid auto-rows-fr grid-cols-1 items-stretch gap-3 md:grid-cols-3">
          {topCompatibleRoles.map((roleFit) => (
            <RoleFitCard key={roleFit.id} isComparisonRole={roleFit.id === comparisonRoleId} roleFit={roleFit} />
          ))}
        </div>
      ) : (
        <p
          className="rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container p-5 text-body-medium text-sys-brand-on-surface-variant"
          data-testid="career-plan-top-compatible-roles-empty"
        >
          {TOP_COMPATIBLE_ROLES_EMPTY_COPY}
        </p>
      )}
    </div>
  );
}
